import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapMarkerData, removeMapData } from "@/common/maps/features";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import { httpGet, httpPost, httpDelete } from "@/services/api.service";
import {
  ENDPOINT_HOTSPOTS,
  ENDPOINT_HOTSPOTS_TOPIC,
  ENDPOINT_HOTSPOTS_TOPIC_TOP,
  ENDPOINT_TOPICS_CLUSTERING_CORRECTION,
} from "@/common/constants/api";
import { GRIDS_HOTSPOTS_LEVELS } from "@/common/constants/grids";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { parseStringToObject } from "@/common/basic.utilities";
import { getFormattedDate } from "@/common/date.utilities";
import { getGeohashByGeometry } from "@/common/maps/utilities";
import { getCityNearGeohash } from "@/services/grids/grids.service";
import { findTopic } from "@/services/events/topics.service";
import { getAllHotspotTopics } from "../events/topics.service";

export { _getHotspots as getHotspots };
export { _getHotspotsByGeohash as getHotspotsByGeohash };
export { _moveHotspot as moveHotspot };
export { _deleteHotspot as deleteHotspot };
export { _getHistograms as getHistograms };
export { _formatFeatures as formatFeatures };
export { _formatFeature as formatFeature };
export { _addToMap as addToMap };
export { _buildPreferences as buildPreferences };
export { _getLevelsFromPreferences as getLevelsFromPreferences };

const _getHotspots = async (params) => {
  try {
    let url;
    let otherUrl;

    if (params.next) {
      url = params.next;
    } else {
      params.pageSize = 1000;

      // this "endpoint" variable was never being used, delete or actually start using it?
      // let endpoint = ENDPOINT_HOTSPOTS_TOPIC_TOP;
      // if (params.topic) {
      //   endpoint = `${ENDPOINT_HOTSPOTS_TOPIC}/${params.topic}`;
      // }
      // url = ENDPOINT_HOTSPOTS_TOPIC_TOP + QUERY;
      url = encodeAllURLParameters({
        baseEndpoint: ENDPOINT_HOTSPOTS_TOPIC_TOP,
        params: params,
      });
    }

    const RESULT = await httpGet({ url: encodeURI(url) });
    const { data } = JSON.parse(RESULT);

    let hotspots = data.features;

    if (!params.ignoreNext) {
      if (!params.hotspots) {
        params.hotspots = [];
      }
      hotspots = data.features.concat(params.hotspots);
    } else {
      hotspots = data.features;
    }
    if (!params.ignoreNext && data.metadata && data.metadata.next) {
      params.next = data.metadata.next;
      params.hotspots = hotspots;
      await _getHotspots(params);
    }

    await _formatFeatures(hotspots, "hotspots", params);

    return data;
  } catch (error) {
    console.error(error);
    Promise.reject("Issue getting hotspots in hotspots.service.js");
  }
};

const _getHotspotsByGeohash = async (params) => {
  try {
    const URL = encodeAllURLParameters({
      baseEndpoint: `${ENDPOINT_HOTSPOTS_TOPIC_TOP}/${params.aoi}`,
      params: params,
    });

    const RESULT = await httpGet({ url: URL });
    const { data } = JSON.parse(RESULT);

    let hotspots = data.features;

    await _formatFeatures(hotspots);

    return data;
  } catch (error) {
    Promise.reject("Issue getting hotspots by geohash in hotspots.service.js");
  }
};

const _moveHotspot = async (params, clusterId) => {
  await httpPost({
    url: ENDPOINT_TOPICS_CLUSTERING_CORRECTION + "/" + clusterId,
    data: params,
  });
};

const _deleteHotspot = async (geohash) => {
  await httpDelete({
    url: ENDPOINT_HOTSPOTS + "/" + geohash,
  });
};

const _getHistograms = async (params) => {
  try {
    const RESULT = await _getHotspotsByGeohash(params);
    return RESULT && RESULT.metadata && RESULT.metadata.histogram
      ? RESULT.metadata.histogram
      : [];
  } catch (error) {
    console.log(error);
    Promise.reject(
      "Unable to retrieve histograms in hotspot.service: " + error
    );
  }
};

const _formatFeatures = async (features, type, params) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    await _formatFeature(feature, type, params);
  }
};

const _formatFeature = async (feature, type, params) => {
  if (feature.properties) {
    if (!feature.properties._id) {
      feature.properties._id = feature.properties.geohash || null;
    }
    feature.properties.formatted = {};
    feature.properties.formatted.type = type || "hotspots";
    feature.properties.formatted.startTime = getFormattedDate(
      feature.properties.startTime
    );

    let topics = feature.properties.topics;
    if (typeof topics === "string" || topics instanceof String) {
      feature.properties = parseStringToObject(feature.properties, "topics");
    }
    feature.properties.formatted.topics = _getTopics(feature.properties.topics);

    feature.properties.formatted.location = null;

    if (
      lodashGet(feature, "properties.location_metadata.method") &&
      feature.properties.location_metadata.method.toLowerCase() === "cliff"
    ) {
      let locParts = [];
      if (lodashGet(feature, "properties.location_metadata.city")) {
        locParts.push(feature.properties.location_metadata.city);
      }
      if (lodashGet(feature, "properties.location_metadata.state")) {
        locParts.push(feature.properties.location_metadata.state);
      }
      if (lodashGet(feature, "properties.location_metadata.country")) {
        locParts.push(feature.properties.location_metadata.country);
      }
      feature.properties.formatted.location = locParts.join(", ");
    }

    if (
      feature.geometry &&
      feature.geometry.coordinates &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length === 2
    ) {
      feature.properties.formatted.location = feature.geometry.coordinates;
    }

    feature.properties.formatted.text = null;
    if (params.includeText) {
      feature.properties.formatted.text = await _getFormattedText(
        feature,
        params
      );
    }
  }
};

const _getTopics = (params) => {
  let topics = [];
  if (params && Array.isArray(params)) {
    params.forEach((key) => {
      const TOPIC = findTopic(key, true, false);
      if (TOPIC) {
        topics.push(TOPIC);
      }
    });
  }
  return topics;
};

const _addToMap = (params) => {
  if (params) {
    removeMapData(params.map, params.layerId);

    let endpoint = ENDPOINT_HOTSPOTS_TOPIC_TOP;
    if (params.type && params.type === "hotspotsCovid") {
      endpoint = `${ENDPOINT_HOTSPOTS_TOPIC}/covid19`;
    }

    params.pageSize = 10000;
    const MARKER_ENDPOINT = encodeAllURLParameters({
      baseEndpoint: endpoint,
      params: params,
    });

    const PARAMS = {
      id: params.layerId,
      noCluster: true,
      iconColor: params.iconColor,
      iconName: params.iconName,
    };

    const OPTIONS = getDefaultMarkerOptions(PARAMS);

    mapMarkerData({
      id: params.layerId,
      data: MARKER_ENDPOINT,
      mapClickEvent: params.mapClickEvent,
      markerOptions: OPTIONS,
      map: params.map,
      noCluster: params.noCluster,
      clusterMapClickEvent: params.clusterMapClickEvent,
    });
  }
};

const _buildPreferences = (params) => {
  let preferences = lodashCloneDeep(params.preferences);

  preferences.grids = preferences.grids || {};
  preferences.grids.hotspots = preferences.grids.hotspots || {};

  if (params.levels) {
    let levels = [];
    params.levels.forEach((level) => {
      if (level.selected) {
        levels.push(level.id);
      }
    });
    preferences.grids.hotspots.levels = levels;
  }

  return preferences;
};

//TODO this is mostly duplicated code form with _getLevelsForInitialLoad in stability.service
const _getLevelsForInitialLoad = () => {
  let levels = [];
  const LEVELS = lodashCloneDeep(GRIDS_HOTSPOTS_LEVELS);
  LEVELS.forEach((level) => {
    level.selected = false;
    if (level.id < 4) {
      level.selected = true;
    }
    levels.push(level);
  });
  return levels;
};

const _getLevelsFromPreferences = (preferences) => {
  let levels = [];
  if (
    preferences &&
    preferences.grids &&
    preferences.grids.hotspots &&
    preferences.grids.hotspots.levels &&
    Array.isArray(preferences.grids.hotspots.levels)
  ) {
    const LEVELS = lodashCloneDeep(GRIDS_HOTSPOTS_LEVELS);
    LEVELS.forEach((level) => {
      for (let i = 0; i < preferences.grids.hotspots.levels.length; i++) {
        let levelId = preferences.grids.hotspots.levels[i];
        if (levelId === level.id) {
          level.selected = true;
          break;
        }
      }
      level.selected = level.hasOwnProperty("selected")
        ? level.selected
        : false;
      levels.push(level);
    });
  } else {
    levels = _getLevelsForInitialLoad();
  }
  return levels;
};

const _getFormattedText = async (hotspot, params = {}) => {
  const TOPIC_CODES = lodashGet(hotspot, "properties.topics");
  if (TOPIC_CODES && Array.isArray(TOPIC_CODES) && TOPIC_CODES.length) {
    const TOPICS_ARR = getAllHotspotTopics().filter((topic) => {
      if (TOPIC_CODES.indexOf(topic.apiName) > -1) {
        return true;
      }
      return false;
    });
    let topics = [];
    TOPICS_ARR.forEach((topic) => {
      if (lodashGet(topic, "singular")) {
        topics.push(topic.singular);
      }
    });
    let text = topics.join(", ");
    if (topics.length > 1) {
      text += " events ";
    } else {
      text += " event ";
    }
    const COORDS = lodashGet(hotspot, "geometry.coordinates");
    let gHash;
    if (COORDS && Array.isArray(COORDS) && COORDS[0] && COORDS[1]) {
      gHash = getGeohashByGeometry(hotspot.geometry);
    }
    if (gHash) {
      let includeCountry = params.hasOwnProperty("includeCountryInText")
        ? params.includeCountryInText
        : true;
      const LOCATION = await getCityNearGeohash(gHash, 200, includeCountry);
      if (LOCATION) {
        text += ` near ${LOCATION}`;
      }
    }
    text = text.charAt(0).toUpperCase() + text.slice(1);
    return text;
  }
  return null;
};
